import React, { useEffect, useRef, useState } from "react";
import { Camera, Sparkle, Trash, X, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
  "football", "basketball", "cricket", "tennis", "swimming",
  "athletics", "hockey", "rugby", "badminton", "volleyball",
  "boxing", "kabaddi", "general",
];

function scoreClasses(score) {
  const s = Number(score) || 0;
  if (s >= 80) return "text-emerald-600 border-emerald-300 bg-emerald-50";
  if (s >= 60) return "text-amber-600 border-amber-300 bg-amber-50";
  return "text-red-500 border-red-300 bg-red-50";
}

export default function SportsAnalyzer() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ sport: "football", movement: "", athlete_name: "", notes: "" });
  const [result, setResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/sports-coach/analyses")
      .then(({ data }) => setHistory(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const analyze = async () => {
    if (!file) { toast.error("Please upload a photo first"); return; }
    if (!form.movement.trim()) { toast.error("Tell the AI which movement to analyse"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("sport", form.sport);
    fd.append("movement", form.movement.trim());
    fd.append("athlete_name", form.athlete_name.trim());
    fd.append("notes", form.notes.trim());
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await api.post("/sports-coach/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Analysis complete");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setForm({ sport: "football", movement: "", athlete_name: "", notes: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAnalysis = async (id) => {
    if (!window.confirm("Delete this analysis?")) return;
    await api.delete(`/sports-coach/analyses/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="sc-analyzer">
      <div className="mb-4">
        <div className="label-tiny text-slate-500">// AI FORM & TECHNIQUE ANALYZER</div>
        <div className="text-sm text-slate-600 flex items-center gap-1.5"><Sparkle size={12} className="text-cyan-500" /> Upload a technique photo · Gemini 3.1 Pro returns structured coaching feedback.</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upload panel */}
        <div className="border border-slate-200 bg-white p-4 space-y-3" data-testid="sc-analyzer-upload">
          <div className="label-tiny text-slate-600">// 1 · UPLOAD PHOTO</div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="text-sm" data-testid="sc-analyzer-file" />
          {preview && (
            <div className="border border-slate-200 p-2 bg-slate-50">
              <img src={preview} alt="preview" className="max-h-64 mx-auto" />
            </div>
          )}
          <div className="label-tiny text-slate-600 pt-2">// 2 · CONTEXT</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-analyzer-sport">
              {SPORTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}
            </select>
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Movement / skill (e.g. Straight drive, Free throw)" value={form.movement} onChange={(e) => setForm({ ...form, movement: e.target.value })} data-testid="sc-analyzer-movement" />
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Athlete name (optional)" value={form.athlete_name} onChange={(e) => setForm({ ...form, athlete_name: e.target.value })} data-testid="sc-analyzer-athlete" />
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Coach notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {file && <button onClick={reset} className="text-slate-500 hover:text-slate-800 text-xs label-tiny">RESET</button>}
            <button onClick={analyze} disabled={!file || analyzing} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2 flex items-center gap-2" data-testid="sc-analyzer-submit">
              {analyzing ? "ANALYSING…" : <><Sparkle size={12} /> ANALYSE</>}
            </button>
          </div>
        </div>

        {/* Result panel */}
        <div className="border border-slate-200 bg-white p-4" data-testid="sc-analyzer-result">
          {analyzing && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Sparkle size={14} className="text-cyan-500 animate-pulse" />
              Analysing with Gemini 3.1 Pro… ~5–10s
            </div>
          )}
          {!analyzing && !result && (
            <div className="text-sm text-slate-500 text-center py-10">
              <Camera size={32} weight="duotone" className="mx-auto text-slate-400 mb-2" />
              Upload a photo and click ANALYSE to see AI feedback here.
            </div>
          )}
          {!analyzing && result && <AnalysisView data={result} />}
        </div>
      </div>

      {/* History */}
      <div className="mt-8">
        <div className="label-tiny text-slate-500 mb-2">// HISTORY ({history.length})</div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No analyses yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {history.map((a) => (
              <div key={a.analysis_id} onClick={() => setDetail(a)} className="border border-slate-200 bg-white hover:border-cyan-400 p-3 cursor-pointer" data-testid={`sc-analysis-card-${a.analysis_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-heading text-sm truncate">{a.movement || "Technique"}</div>
                  <div className={`text-[10px] label-tiny tracking-widest px-1.5 py-0.5 border ${scoreClasses(a.result?.score)}`}>
                    {a.result?.score ?? "—"}
                  </div>
                </div>
                <div className="text-[10px] tracking-widest text-slate-500">
                  {(a.sport || "general").replace(/_/g, " ").toUpperCase()}
                  {a.athlete_name && <> · {a.athlete_name}</>}
                </div>
                <div className="text-xs text-slate-600 mt-1">{a.result?.verdict || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)} data-testid="sc-analysis-detail">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="font-heading text-xl">{detail.movement || "Technique analysis"}</div>
                <div className="text-[10px] tracking-widest text-cyan-700 mt-1">
                  {(detail.sport || "").replace(/_/g, " ").toUpperCase()} {detail.athlete_name && `· ${detail.athlete_name}`}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { removeAnalysis(detail.analysis_id); setDetail(null); }} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid="sc-analysis-delete"><Trash size={14} /> DELETE</button>
                <button onClick={() => setDetail(null)} data-testid="sc-analysis-detail-close"><X size={20} /></button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {detail.image_url && (
                <img src={`${BACKEND_URL}${detail.image_url}`} alt="analysed" className="max-h-96 mx-auto border border-slate-200" />
              )}
              <AnalysisView data={detail} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalysisView({ data }) {
  const r = data?.result || {};
  return (
    <div className="space-y-4 text-sm" data-testid="sc-analysis-view">
      <div className="flex items-center gap-3">
        <div className={`text-3xl font-heading tabular-nums px-3 py-1 border ${scoreClasses(r.score)}`} data-testid="sc-analysis-score">{r.score ?? "—"}</div>
        <div>
          <div className="label-tiny text-slate-500">VERDICT</div>
          <div className="font-heading">{r.verdict || "—"}</div>
        </div>
      </div>
      {r.strengths?.length > 0 && (
        <div>
          <div className="label-tiny text-emerald-700 mb-1 flex items-center gap-1"><CheckCircle size={12} /> STRENGTHS</div>
          <ul className="list-disc pl-5 space-y-1 text-slate-700">
            {r.strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {r.issues?.length > 0 && (
        <div>
          <div className="label-tiny text-red-700 mb-1 flex items-center gap-1"><Warning size={12} /> ISSUES + FIXES</div>
          <div className="space-y-2">
            {r.issues.map((i, idx) => (
              <div key={idx} className="border border-red-200 bg-red-50 p-2 text-xs">
                <div className="font-heading text-red-800">{i.area}</div>
                <div className="text-slate-700"><span className="font-semibold">Problem:</span> {i.problem}</div>
                <div className="text-slate-700"><span className="font-semibold">Fix:</span> {i.fix}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {r.cues?.length > 0 && (
        <div>
          <div className="label-tiny text-cyan-700 mb-1">COACHING CUES</div>
          <ul className="list-disc pl-5 space-y-1 text-slate-700">
            {r.cues.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {r.recommended_drills?.length > 0 && (
        <div>
          <div className="label-tiny text-cyan-700 mb-1">RECOMMENDED DRILLS</div>
          <div className="flex flex-wrap gap-1">
            {r.recommended_drills.map((d, i) => (
              <span key={i} className="text-xs bg-cyan-100 text-cyan-800 border border-cyan-300 px-2 py-0.5">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
